import React from 'react'
import { Helmet } from 'react-helmet'
import { getBaseURL } from 'services/window'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { FreelancerProfileHead_User$key } from '__generated__/FreelancerProfileHead_User.graphql'

export interface IFreelancerProfileHeadProps {
  userFragmentRef: FreelancerProfileHead_User$key
}

function FreelancerProfileHead({ userFragmentRef }: IFreelancerProfileHeadProps) {
  const freelancer = useFragment(graphql`
    fragment FreelancerProfileHead_User on User {
      firstName
      lastName
      avatarUrl
      email
      profile {
        slug
        industry {
          name
        }
        fullAddress
        region
        country {
          name
        }
        city
        zip
        textIntroduction
      }
      video {
        posterUrl
      }
    }
  `, userFragmentRef)

  if (!freelancer?.profile) {
    return null
  }

  const profile = freelancer.profile
  const name = freelancer.firstName
  const url = `${getBaseURL()}/${profile.slug}`
  const textIntroduction = profile.textIntroduction || `${profile?.industry?.name ?? 'An'} expert on Flexhire`
  const imgUrl = freelancer.video?.posterUrl || freelancer.avatarUrl

  let title = `${name} on Flexhire`

  if (profile.industry?.name) {
    title = `${name} - ${profile.industry.name} expert on Flexhire`
  }

  return (
    <Helmet titleTemplate="%s">
      <title>{title}</title>
      <meta name="description" content={textIntroduction} />
      <meta property="og:title" content={title} />
      <meta property="og:url" content={url} />
      <meta property="og:description" content={textIntroduction} />

      {imgUrl && (
        <meta property="og:image" content={imgUrl} />
      )}

      <meta property="og:image:width" content="120" />
      <meta property="og:image:height" content="120" />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'http://schema.org',
          '@type': 'Person',
          name: `${freelancer.firstName} ${freelancer.lastName}`.trim(),
          description: profile.textIntroduction,
          image: freelancer.avatarUrl,
          jobTitle: profile.industry?.name,
          email: freelancer.email,
          address: {
            '@type': 'PostalAddress',
            streetAddress: profile.fullAddress,
            addressLocality: profile.city,
            addressRegion: profile.region,
            ...(profile.zip && { postalCode: profile.zip }),
            addressCountry: profile.country?.name,
          },
          url,
        })}
      </script>
    </Helmet>
  )
}

export default React.memo(FreelancerProfileHead)
